import React, { useState } from "react";
import { Table, Tag, Input, Empty } from "antd";
import { Search, RotateCcw, Calendar } from "lucide-react";
import { useDebounce } from "use-debounce";
import dayjs from "dayjs";
import { useOrderReturnsQuery } from "../../Services";
import { PageHeader, tablePagination } from "../../components";
import { PAGE_SIZE, DEBOUNCED_SEARCH_QUERY } from "../../Constant";
import { ReturnedItemsSection } from "./common";

export const ReturnsHistory: React.FC = () => {
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState("");
  const [search] = useDebounce(searchInput, DEBOUNCED_SEARCH_QUERY);
  // Queries
  const {
    data: returnsList,
    isLoading,
    refetch,
  } = useOrderReturnsQuery({
    page,
    limit: PAGE_SIZE,
    search,
  });

  const totalReturns = returnsList?.total || 0;

  // Table Columns
  const columns = [
    {
      title: "Date & Time",
      dataIndex: "return_date",
      key: "return_date",
      render: (v: string) => (
        <span className="text-xs text-gray-600 flex items-center gap-1.5 font-medium">
          <Calendar className="w-3.5 h-3.5 text-gray-400" />
          {dayjs(v).format("YYYY-MM-DD HH:mm")}
        </span>
      ),
    },
    {
      title: "Return No",
      dataIndex: "return_no",
      key: "return_no",
      render: (v: string) => (
        <span className="font-bold text-gray-800 text-xs">{v}</span>
      ),
    },
    {
      title: "Order No",
      dataIndex: "order_no",
      key: "order_no",
      render: (v: string) => (
        <span className="font-semibold text-blue-600 text-xs">{v}</span>
      ),
    },
    {
      title: "Customer",
      dataIndex: "customer_name",
      key: "customer_name",
      render: (v: string) => (
        <span className="text-gray-700 text-xs font-medium">
          {v || "Walk-in Customer"}
        </span>
      ),
    },
    {
      title: "Refund Type",
      dataIndex: "refund_type_name",
      key: "refund_type_name",
      render: (v: string) => (
        <Tag color="purple" className="text-[10px] font-bold uppercase">
          {v || "Cash Refund"}
        </Tag>
      ),
    },
    {
      title: "Payment Method",
      dataIndex: "refund_payment_method_name",
      key: "refund_payment_method_name",
      render: (v: string) => (
        <span className="text-xs text-gray-600 font-medium">{v || "—"}</span>
      ),
    },
    {
      title: "Refund Amount",
      dataIndex: "total_refunded",
      key: "total_refunded",
      render: (v: number) => (
        <span className="font-bold text-rose-600 text-xs">
          LKR {v.toLocaleString(undefined, { minimumFractionDigits: 2 })}
        </span>
      ),
    },
  ];

  return (
    <div className="p-6 min-h-screen bg-gray-50">
      <PageHeader
        title="Order Returns History"
        subText={`Total return records: ${totalReturns}`}
        handleButtons={[
          { title: "", icon: RotateCcw as any, onClick: refetch },
        ]}
        searchBar={
          <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
            <Input
              placeholder="Search Return No, Order No, Customer..."
              prefix={<Search className="w-3.5 h-3.5 text-gray-400 mr-1" />}
              value={searchInput}
              onChange={(e) => {
                setSearchInput(e.target.value);
                setPage(1);
              }}
              allowClear
              onClear={() => {
                setSearchInput("");
                setPage(1);
              }}
              className="w-full sm:w-72 h-9 rounded-md text-xs"
            />
          </div>
        }
      />

      <div className="mt-4 bg-white rounded-lg border border-gray-200 overflow-hidden shadow-sm">
        <Table
          dataSource={returnsList?.data || []}
          columns={columns}
          rowKey="id"
          loading={isLoading}
          pagination={tablePagination({
            current: page,
            pageSize: PAGE_SIZE,
            total: totalReturns,
            onChange: setPage,
            itemName: "returns",
          })}
          scroll={{ x: 1000 }}
          expandable={{
            expandedRowRender: (record: any) => (
              <ReturnedItemsSection items={record.items} note={record.note} />
            ),
          }}
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="No returns logged yet."
              />
            ),
          }}
        />
      </div>
    </div>
  );
};
